import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Zap, Tractor, Wrench, ArrowLeft, Clock, DollarSign, Weight, Info, Leaf } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { "Trailers": Truck, "Generators": Zap, "Tractors": Tractor, "Tractor Attachments": Wrench, "Lawn Care": Leaf };

export default function EquipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rentalType, setRentalType] = useState("daily");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/equipment/${id}`),
      axios.get(`${API}/equipment/${id}/availability`)
    ]).then(([equipRes, availRes]) => {
      setItem(equipRes.data);
      // Build array of booked Date objects
      const ranges = availRes.data.booked_ranges || [];
      const dates = [];
      ranges.forEach(range => {
        const start = new Date(range.start_date);
        const end = new Date(range.end_date);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }
      });
      setBookedDates(dates);
    }).catch(() => toast.error("Equipment not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const days = startDate && endDate ? Math.max(Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)), 1) : 0;
  const cost = item ? (rentalType === "weekly" ? item.weekly_rate * Math.max(Math.ceil(days / 7), 1) : item.daily_rate * days) : 0;
  const deposit = item?.deposit || 0;
  const total = cost + deposit;

  const isDateBooked = (date) => {
    return bookedDates.some(d =>
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  };

  const handleBook = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    // Guest validation
    if (!user) {
      if (!guestName.trim() || !guestEmail.trim() || !guestPhone.trim()) {
        toast.error("Please fill in your name, email, and phone number");
        return;
      }
    }
    setBooking(true);
    try {
      let bookingData;
      let checkoutData;

      if (user) {
        // Logged-in user flow
        const res = await axios.post(
          `${API}/bookings`,
          {
            equipment_id: id,
            start_date: startDate.toISOString().split("T")[0],
            end_date: endDate.toISOString().split("T")[0],
            rental_type: rentalType,
            notes,
          },
          { withCredentials: true }
        );
        bookingData = res.data;
        const checkoutRes = await axios.post(
          `${API}/payments/checkout`,
          { booking_id: bookingData.id, origin_url: window.location.origin },
          { withCredentials: true }
        );
        checkoutData = checkoutRes.data;
      } else {
        // Guest flow
        const res = await axios.post(`${API}/bookings/guest`, {
          equipment_id: id,
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
          rental_type: rentalType,
          notes,
          guest_name: guestName.trim(),
          guest_email: guestEmail.trim(),
          guest_phone: guestPhone.trim(),
        });
        bookingData = res.data;
        const checkoutRes = await axios.post(`${API}/payments/guest-checkout`, {
          booking_id: bookingData.id,
          origin_url: window.location.origin,
        });
        checkoutData = checkoutRes.data;
      }

      // Redirect to Stripe
      window.location.href = checkoutData.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="w-8 h-8 border-4 border-[#E68A00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <p className="text-[#595959]">Equipment not found.</p>
      </div>
    );
  }

  const Icon = ICONS[item.category] || Truck;

  return (
    <div data-testid="equipment-detail" className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <button
          onClick={() => navigate("/equipment")}
          data-testid="back-to-catalog"
          className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#1A2F24] transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Equipment
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left: Image + Info */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-[#E5E0D8] rounded-lg overflow-hidden mb-6">
              <div className="relative h-72 sm:h-96 bg-[#EFECE5]">
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                <Badge className={`absolute top-4 left-4 ${item.available ? 'bg-green-600' : 'bg-red-600'} text-white`}>
                  {item.available ? "Available" : "Rented Out"}
                </Badge>
              </div>
            </div>

            <div className="bg-white border border-[#E5E0D8] rounded-lg p-6">
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-5 h-5 text-[#E68A00]" />
                <span className="text-sm font-medium text-[#595959] uppercase tracking-wider">{item.category}</span>
              </div>
              <h1
                className="text-2xl sm:text-3xl font-bold text-[#1A2F24] mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="equipment-name"
              >
                {item.name}
              </h1>
              <p className="text-[#595959] leading-relaxed mb-6">{item.description}</p>

              <Separator className="my-5 bg-[#E5E0D8]" />

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {item.capacity && (
                  <div className="flex items-start gap-2">
                    <Weight className="w-4 h-4 text-[#E68A00] mt-0.5" />
                    <div>
                      <p className="text-xs text-[#595959] uppercase tracking-wider">Capacity</p>
                      <p className="text-sm font-medium text-[#1C1C1C]">{item.capacity}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#E68A00] mt-0.5" />
                  <div>
                    <p className="text-xs text-[#595959] uppercase tracking-wider">Daily Rate</p>
                    <p className="text-sm font-medium text-[#1C1C1C]">${item.daily_rate}/day</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <DollarSign className="w-4 h-4 text-[#E68A00] mt-0.5" />
                  <div>
                    <p className="text-xs text-[#595959] uppercase tracking-wider">Weekly Rate</p>
                    <p className="text-sm font-medium text-[#1C1C1C]">${item.weekly_rate}/week</p>
                  </div>
                </div>
              </div>

              {item.details && (
                <div className="mt-5 p-4 bg-[#F9F8F6] rounded-md border border-[#E5E0D8]">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#E68A00] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[#595959]">{item.details}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Booking Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E5E0D8] rounded-lg p-6 sticky top-24">
              <h3
                className="text-xl font-semibold text-[#1A2F24] mb-5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Book This Equipment
              </h3>

              {/* Rental Type */}
              <div className="mb-4">
                <label className="text-sm font-medium text-[#1C1C1C] mb-1.5 block">Rental Type</label>
                <Select value={rentalType} onValueChange={setRentalType} data-testid="rental-type-select">
                  <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="rental-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily (${item.daily_rate}/day)</SelectItem>
                    <SelectItem value="weekly">Weekly (${item.weekly_rate}/week)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div className="mb-4">
                <label className="text-sm font-medium text-[#1C1C1C] mb-1.5 block">Start Date</label>
                <div className="border border-[#E5E0D8] rounded-md overflow-hidden">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(d) => { setStartDate(d); if (endDate && d > endDate) setEndDate(null); }}
                    disabled={(date) => date < new Date() || isDateBooked(date)}
                    className="w-full"
                    data-testid="start-date-calendar"
                  />
                </div>
                {bookedDates.length > 0 && (
                  <p className="text-xs text-[#595959] mt-1.5 flex items-center gap-1">
                    <span className="inline-block w-3 h-3 bg-gray-200 rounded-sm" /> = Already booked
                  </p>
                )}
              </div>

              {/* End Date */}
              <div className="mb-5">
                <label className="text-sm font-medium text-[#1C1C1C] mb-1.5 block">End Date</label>
                <div className="border border-[#E5E0D8] rounded-md overflow-hidden">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    disabled={(date) => date < (startDate || new Date()) || isDateBooked(date)}
                    className="w-full"
                    data-testid="end-date-calendar"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="mb-5">
                <label className="text-sm font-medium text-[#1C1C1C] mb-1.5 block">Notes (optional)</label>
                <textarea
                  data-testid="booking-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requirements..."
                  className="w-full p-3 border border-[#E5E0D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E68A00] resize-none h-20 bg-white"
                />
              </div>

              {/* Guest Info (shown when not logged in) */}
              {!user && (
                <div className="mb-5 p-4 bg-[#F9F8F6] border border-[#E5E0D8] rounded-md" data-testid="guest-info-form">
                  <p className="text-sm font-semibold text-[#1A2F24] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Your Contact Info
                  </p>
                  <div className="space-y-3">
                    <input
                      data-testid="guest-name"
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Full Name *"
                      className="w-full p-2.5 border border-[#E5E0D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E68A00] bg-white"
                    />
                    <input
                      data-testid="guest-email"
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="Email Address *"
                      className="w-full p-2.5 border border-[#E5E0D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E68A00] bg-white"
                    />
                    <input
                      data-testid="guest-phone"
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      placeholder="Phone Number *"
                      className="w-full p-2.5 border border-[#E5E0D8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E68A00] bg-white"
                    />
                  </div>
                  <p className="text-xs text-[#595959] mt-2">
                    Or <a href="/login" className="text-[#E68A00] underline">log in</a> to track your rentals.
                  </p>
                </div>
              )}

              {/* Cost Summary */}
              {startDate && endDate && (
                <div className="bg-[#F9F8F6] border border-[#E5E0D8] rounded-md p-4 mb-5" data-testid="cost-summary">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-[#595959]">
                      <span>Rental ({days} {rentalType === "weekly" ? `week${Math.ceil(days/7) > 1 ? 's' : ''}` : `day${days > 1 ? 's' : ''}`})</span>
                      <span>${cost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#595959]">
                      <span>Refundable Deposit</span>
                      <span>${deposit.toFixed(2)}</span>
                    </div>
                    <Separator className="my-2 bg-[#E5E0D8]" />
                    <div className="flex justify-between font-semibold text-[#1A2F24]">
                      <span>Total</span>
                      <span className="text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <button
                data-testid="book-now-button"
                onClick={handleBook}
                disabled={booking || !item.available}
                className="w-full bg-[#E68A00] hover:bg-[#CC7A00] disabled:bg-[#E5E0D8] disabled:text-[#595959] text-white font-semibold py-3.5 rounded-md transition-colors"
              >
                {booking ? "Processing..." : !item.available ? "Currently Unavailable" : "Book & Pay Now"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
